import React, { useState, useMemo, Suspense } from 'react';
import { Routes, Route } from 'react-router-dom';
import { Box, Typography, CircularProgress } from '@mui/material';

import { useAuth } from '../services/AuthContext';
import MainLayout from '../components/layout/MainLayout';
import PrivateRoute from '../components/PrivateRoute';

import HomeIcon from '@mui/icons-material/Home';
import PersonIcon from '@mui/icons-material/Person';
import PeopleIcon from '@mui/icons-material/People';
import BuildIcon from '@mui/icons-material/Build';
import AssignmentIcon from '@mui/icons-material/Assignment';
import EditIcon from '@mui/icons-material/Edit';
import StoreIcon from '@mui/icons-material/Store';
import BadgeIcon from '@mui/icons-material/Badge';
import DevicesIcon from '@mui/icons-material/Devices';
import AssessmentIcon from '@mui/icons-material/Assessment';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import SettingsIcon from '@mui/icons-material/Settings';
import RemoveShoppingCartIcon from '@mui/icons-material/RemoveShoppingCart';
import { lazyWithRetry } from '../utils/lazyWithRetry';

/** טעינה עצלה: כל דף נטען רק כשנכנסים אליו — חותך משמעותית את זמן הטעינה הראשונה. */
const Dashboard = lazyWithRetry(() => import('../pages/Dashboard'));
const CustomersPage = lazyWithRetry(() => import('../pages/CustomersPage'));
const ServiceCallsPage = lazyWithRetry(() => import('../pages/ServiceCallsPage'));
const Stores = lazyWithRetry(() => import('../pages/StoresPage'));
const EmployeesPage = lazyWithRetry(() => import('../pages/EmployeesPage'));
const ExternalUsersPage = lazyWithRetry(() => import('../pages/ExternalUsersPage'));
const DevicesPage = lazyWithRetry(() => import('../pages/DevicesPage'));
const SparePartsPage = lazyWithRetry(() => import('../pages/SparePartsPage'));
const InventoryIntakePage = lazyWithRetry(() => import('../pages/InventoryIntakePage'));
const InventoryCountPage = lazyWithRetry(() => import('../pages/InventoryCountPage'));
const InventoryOutboundPageLazy = lazyWithRetry(() => import('../pages/InventoryOutboundPage'));
const WhatsappLeadsPage = lazyWithRetry(() => import('../pages/WhatsappLeadsPage'));
const ReportsPage = lazyWithRetry(() => import('../pages/ReportsPage'));
const KnowledgeBasePage = lazyWithRetry(() => import('../pages/KnowledgeBasePage'));
const KnowledgeBaseViewPage = lazyWithRetry(() => import('../pages/KnowledgeBaseViewPage'));
const KnowledgeBaseFormPage = lazyWithRetry(() => import('../pages/KnowledgeBaseFormPage'));
const SettingsPage = lazyWithRetry(() => import('../pages/SettingsPage'));

const RouteLoader: React.FC = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
    <CircularProgress size={28} />
  </Box>
);

const ServiceCallsMenuIcon = (
  <Box sx={{ position: 'relative', width: 24, height: 24 }}>
    <AssignmentIcon sx={{ fontSize: 22 }} />
    <EditIcon
      sx={{
        position: 'absolute',
        fontSize: 11,
        bottom: 0,
        right: -1,
        color: 'primary.light',
      }}
    />
  </Box>
);

// Base menu items that are always available (excluding external_viewer)
const BASE_MENU_ITEMS = [
  { text: 'דשבורד', path: '/', icon: <HomeIcon /> },
  { text: 'קריאות שירות', path: '/ServiceCallsPage', icon: ServiceCallsMenuIcon },
  { text: 'מאגר ידע', path: '/knowledge-base', icon: <MenuBookIcon /> },
];

const WAREHOUSE_MENU_ITEMS = [
  { text: 'דשבורד', path: '/', icon: <HomeIcon /> },
  { text: 'מאגר ידע', path: '/knowledge-base', icon: <MenuBookIcon /> },
  { text: 'הוצאת סחורה מהמלאי', path: '/inventory/outbound', icon: <RemoveShoppingCartIcon /> },
];

const WHATSAPP_MENU_ITEM = { text: 'פניות מהווצאפ', path: '/whatsapp-leads', icon: <WhatsAppIcon /> };

// Manager menu items (for service role)
const MANAGER_MENU_ITEMS = [
  { text: 'לקוחות', path: '/CustomersPage', icon: <PersonIcon /> },
  { text: 'חנויות', path: '/StoresPage', icon: <StoreIcon /> },
  { text: 'הגדרות', path: '/settings', icon: <SettingsIcon /> },
];

// Admin-only menu items (includes devices)
const ADMIN_ADDITIONAL_MENU_ITEMS = [
  { text: 'מכשירים', path: '/DevicesPage', icon: <DevicesIcon /> },
  { text: 'חלקי חילוף', path: '/spare-parts', icon: <BuildIcon /> },
  { text: 'הוצאת סחורה מהמלאי', path: '/inventory/outbound', icon: <RemoveShoppingCartIcon /> },
  { text: 'דוחות', path: '/Reports', icon: <AssessmentIcon /> },
];

// Admin-only menu items
const ADMIN_MENU_ITEMS = [
  { text: 'עובדים', path: '/EmployeesPage', icon: <BadgeIcon /> },
  { text: 'משתמשים חיצוניים', path: '/ExternalUsersPage', icon: <PeopleIcon /> },
];

const AppContent = React.memo(() => {
  // Start with drawer closed on mobile by detecting screen size
  const [drawerOpen, setDrawerOpen] = useState(false);
  const toggleDrawer = () => setDrawerOpen(prev => !prev);

  const { loading, role, isAdmin, isManager } = useAuth();

  // Memoize menu items to prevent unnecessary recalculations
  const menuItems = useMemo(() => {
    // External viewers don't see knowledge base
    if (role === 'external_viewer') {
      return [
        { text: 'דשבורד', path: '/', icon: <HomeIcon /> },
        { text: 'קריאות שירות', path: '/ServiceCallsPage', icon: ServiceCallsMenuIcon },
      ];
    }

    if (role === 'warehouse') {
      return [...WAREHOUSE_MENU_ITEMS];
    }

    let items = [...BASE_MENU_ITEMS];
    
    if (isManager || isAdmin) {
      items = [...items, WHATSAPP_MENU_ITEM, ...MANAGER_MENU_ITEMS];
    }
    
    if (isAdmin) {
      items = [...items, ...ADMIN_ADDITIONAL_MENU_ITEMS];
      items = [...items, ...ADMIN_MENU_ITEMS];
    }
    
    return items;
  }, [isManager, isAdmin, role]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <Typography>טוען...</Typography>
      </Box>
    );
  }

  return (
    <MainLayout
      open={drawerOpen}
      toggleDrawer={toggleDrawer}
      drawerWidth={240}
      closedWidth={60}
      menuItems={menuItems}
    >
      <Suspense fallback={<RouteLoader />}>
        <Routes>
          {/* Routes for all authenticated users */}
          <Route path="/" element={<PrivateRoute element={<Dashboard />} />} />
          <Route path="/dashboard" element={<PrivateRoute element={<Dashboard />} />} />
          <Route path="/ServiceCallsPage" element={<PrivateRoute element={<ServiceCallsPage />} />} />

          {/* Knowledge Base routes - all roles except external_viewer */}
          <Route path="/knowledge-base" element={<PrivateRoute element={<KnowledgeBasePage />} />} />
          <Route path="/knowledge-base/:id" element={<PrivateRoute element={<KnowledgeBaseViewPage />} />} />
          <Route path="/knowledge-base/edit/:id" element={<PrivateRoute element={<KnowledgeBaseFormPage />} />} />
          <Route path="/knowledge-base/new" element={<PrivateRoute element={<KnowledgeBaseFormPage />} />} />

          {/* Routes for service/admin */}
          <Route path="/whatsapp-leads" element={<PrivateRoute element={<WhatsappLeadsPage />} />} />
          <Route path="/CustomersPage" element={<PrivateRoute element={<CustomersPage />} />} />
          <Route path="/StoresPage" element={<PrivateRoute element={<Stores />} />} />
          <Route path="/settings" element={<PrivateRoute element={<SettingsPage />} />} />
          <Route path="/DevicesPage" element={<PrivateRoute element={<DevicesPage />} />} />
          <Route path="/inventory/intake" element={<PrivateRoute element={<InventoryIntakePage />} />} />
          <Route path="/inventory/count" element={<PrivateRoute element={<InventoryCountPage />} />} />
          <Route path="/inventory/outbound" element={<PrivateRoute role="warehouse" element={<InventoryOutboundPageLazy />} />} />
          <Route path="/inventory/outbound/history" element={<PrivateRoute role="warehouse" element={<InventoryOutboundPageLazy />} />} />

          {/* Admin-only routes */}
          <Route path="/EmployeesPage" element={<PrivateRoute role="admin" element={<EmployeesPage />} />} />
          <Route path="/ExternalUsersPage" element={<PrivateRoute role="admin" element={<ExternalUsersPage />} />} />
          <Route path="/Reports" element={<PrivateRoute role="admin" element={<ReportsPage />} />} />
          <Route path="/spare-parts" element={<PrivateRoute element={<SparePartsPage />} />} />
          <Route path="/spare-parts/:deviceId" element={<PrivateRoute element={<SparePartsPage />} />} />
        </Routes>
      </Suspense>
    </MainLayout>
  );
});

AppContent.displayName = 'AppContent';

export default AppContent;
